import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Download, Loader2, Printer, ScanLine, Star } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  LABEL_FORMAT_OPTIONS,
  blobFromAxiosResponse,
  buildDeliveryLabelDownloadEndpoint,
  buildDeliveryLabelPreviewEndpoint,
  downloadBlobFile,
  getDeliveryLabelCapabilities,
  getLabelFormatOption,
  normalizeLabelFormat,
  printPdfBlobUrl,
  readBlobErrorMessage,
  resolveDeliveryLabelFilename,
} from '@/utils/deliveryLabels';

const DeliveryLabelDialog = ({
  open,
  onOpenChange,
  delivery,
  allowPreferredFormat = false,
}) => {
  const previewUrlRef = useRef('');
  const [selectedFormat, setSelectedFormat] = useState('');
  const [preferredFormat, setPreferredFormat] = useState('');
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [savingPreferredFormat, setSavingPreferredFormat] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);

  const labelCapabilities = useMemo(
    () => getDeliveryLabelCapabilities(delivery),
    [delivery]
  );

  const availableFormats = useMemo(() => {
    const supported = new Set((labelCapabilities.supportedFormats || []).map(normalizeLabelFormat));
    return LABEL_FORMAT_OPTIONS.filter((option) => supported.has(option.code));
  }, [labelCapabilities]);

  const labelReference = delivery?.tracking_code || `#${String(delivery?.id || '').slice(0, 8)}`;
  const isSelectedPreferred = Boolean(selectedFormat) && selectedFormat === preferredFormat;

  const replacePreviewUrl = (nextUrl = '') => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    previewUrlRef.current = nextUrl;
    setPreviewUrl(nextUrl);
  };

  useEffect(() => () => replacePreviewUrl(''), []);

  useEffect(() => {
    if (!open) {
      replacePreviewUrl('');
      setPreviewError('');
      setLoadingPreview(false);
      setSelectedFormat('');
      setPreferredFormat('');
      setLoadingPreferences(false);
      setSavingPreferredFormat(false);
      setDownloading(false);
      setPrinting(false);
      return;
    }

    let active = true;

    const initialize = async () => {
      const defaultFormat = normalizeLabelFormat(availableFormats[0]?.code || 'A');

      if (!allowPreferredFormat) {
        if (active) {
          setPreferredFormat('');
          setSelectedFormat(defaultFormat);
        }
        return;
      }

      setLoadingPreferences(true);
      try {
        const response = await axios.get(`${API}/user/label-preferences`);
        if (!active) {
          return;
        }
        const backendPreferred = normalizeLabelFormat(
          response.data?.data?.preferences?.preferred_format || defaultFormat
        );
        setPreferredFormat(backendPreferred);
        setSelectedFormat(backendPreferred);
      } catch (_error) {
        if (active) {
          setPreferredFormat('');
          setSelectedFormat(defaultFormat);
        }
      } finally {
        if (active) {
          setLoadingPreferences(false);
        }
      }
    };

    initialize();

    return () => {
      active = false;
    };
  }, [allowPreferredFormat, availableFormats, open]);

  useEffect(() => {
    if (!open || !delivery?.id || !selectedFormat || !labelCapabilities.available) {
      return undefined;
    }

    let active = true;
    setLoadingPreview(true);
    setPreviewError('');

    axios.get(buildDeliveryLabelPreviewEndpoint(delivery.id, selectedFormat), {
      responseType: 'blob',
    })
      .then((response) => {
        if (!active) {
          return;
        }
        const blob = blobFromAxiosResponse(response.data);
        const nextObjectUrl = window.URL.createObjectURL(blob);
        replacePreviewUrl(nextObjectUrl);
      })
      .catch(async (error) => {
        if (!active) {
          return;
        }
        replacePreviewUrl('');
        setPreviewError(
          await readBlobErrorMessage(error, "Impossible de charger l'aperçu de l'étiquette.")
        );
      })
      .finally(() => {
        if (active) {
          setLoadingPreview(false);
        }
      });

    return () => {
      active = false;
    };
  }, [delivery?.id, labelCapabilities.available, open, selectedFormat]);

  const handleSavePreferredFormat = async () => {
    if (!allowPreferredFormat || !selectedFormat || isSelectedPreferred) {
      return;
    }

    setSavingPreferredFormat(true);
    try {
      const response = await axios.patch(`${API}/user/label-preferences`, {
        preferred_format: selectedFormat,
      });
      const nextPreferred = normalizeLabelFormat(
        response.data?.data?.preferences?.preferred_format || selectedFormat
      );
      setPreferredFormat(nextPreferred);
      toast.success('Format préféré enregistré.');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d'enregistrer le format préféré.");
    } finally {
      setSavingPreferredFormat(false);
    }
  };

  const handleDownload = async () => {
    if (!delivery?.id || !selectedFormat) {
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.get(
        buildDeliveryLabelDownloadEndpoint(delivery.id, selectedFormat),
        { responseType: 'blob' }
      );
      const blob = blobFromAxiosResponse(response.data);
      const filename = resolveDeliveryLabelFilename({
        headers: response.headers,
        delivery,
        formatCode: selectedFormat,
      });
      downloadBlobFile({ blob, filename });
      toast.success('PDF téléchargé.');
    } catch (error) {
      toast.error(
        await readBlobErrorMessage(error, "Impossible de télécharger l'étiquette PDF.")
      );
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedFormat || !delivery?.id) {
      return;
    }

    setPrinting(true);
    try {
      let urlForPrint = previewUrl;
      let temporaryObjectUrl = '';

      if (!urlForPrint) {
        const response = await axios.get(
          buildDeliveryLabelPreviewEndpoint(delivery.id, selectedFormat),
          { responseType: 'blob' }
        );
        const blob = blobFromAxiosResponse(response.data);
        temporaryObjectUrl = window.URL.createObjectURL(blob);
        urlForPrint = temporaryObjectUrl;
      }

      await printPdfBlobUrl(urlForPrint);
      toast.success('Fenêtre d’impression ouverte. Pensez à imprimer en taille réelle / 100%.');

      if (temporaryObjectUrl) {
        window.setTimeout(() => {
          window.URL.revokeObjectURL(temporaryObjectUrl);
        }, 1500);
      }
    } catch (error) {
      toast.error(
        await readBlobErrorMessage(error, "Impossible d'ouvrir l'impression de l'étiquette.")
      );
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-red-600" />
            Étiquette
          </DialogTitle>
          <DialogDescription>
            Référence {labelReference}. Choisissez un format, prévisualisez le PDF, puis téléchargez-le ou imprimez-le.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-900">Format</p>
              <div className="mt-3 space-y-2">
                {availableFormats.map((formatOption) => {
                  const isActive = selectedFormat === formatOption.code;
                  return (
                    <button
                      key={formatOption.code}
                      type="button"
                      data-testid={`label-format-${formatOption.code}`}
                      className={`w-full rounded-lg border px-3 py-3 text-left transition ${
                        isActive
                          ? 'border-red-300 bg-red-50 text-red-700 shadow-sm'
                          : 'border-slate-200 bg-white hover:border-red-200 hover:bg-red-50/60'
                      }`}
                      onClick={() => setSelectedFormat(formatOption.code)}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium">{formatOption.title}</p>
                          <p className="text-xs text-slate-500">{formatOption.description}</p>
                        </div>
                        {preferredFormat === formatOption.code && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Star className="mr-1 h-3 w-3" />
                            Préféré
                          </Badge>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {allowPreferredFormat && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    data-testid="preferred-format-checkbox"
                    checked={isSelectedPreferred}
                    disabled={loadingPreferences || savingPreferredFormat || !selectedFormat}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        handleSavePreferredFormat();
                      }
                    }}
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Définir comme format préféré</p>
                    <p className="text-xs text-slate-500">
                      Le format préféré est présélectionné automatiquement à l’ouverture.
                    </p>
                    {selectedFormat && (
                      <p className="mt-2 text-xs text-slate-600">
                        Format sélectionné: {getLabelFormatOption(selectedFormat).name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Pour un meilleur rendu, imprimez en taille réelle / 100%.
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Aperçu</p>
                <p className="text-xs text-slate-500">
                  {selectedFormat
                    ? `${getLabelFormatOption(selectedFormat).title} prêt à être prévisualisé.`
                    : 'Sélectionnez un format pour charger l’aperçu.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="download-label-btn"
                  onClick={handleDownload}
                  disabled={!labelCapabilities.available || !selectedFormat || downloading}
                >
                  {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Télécharger PDF
                </Button>
                <Button
                  type="button"
                  className="bg-red-500 hover:bg-red-600"
                  data-testid="print-label-btn"
                  onClick={handlePrint}
                  disabled={!labelCapabilities.available || !selectedFormat || printing}
                >
                  {printing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
                  Imprimer
                </Button>
              </div>
            </div>

            {!labelCapabilities.available ? (
              <div
                data-testid="label-unavailable-state"
                className="flex min-h-[420px] items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center"
              >
                <div className="max-w-md">
                  <p className="text-lg font-semibold text-slate-900">Étiquette indisponible</p>
                  <p className="mt-2 text-sm text-slate-600">
                    {labelCapabilities.reason || "Cette livraison n'est pas encore éligible à l'étiquette."}
                  </p>
                </div>
              </div>
            ) : loadingPreview || loadingPreferences ? (
              <div className="flex min-h-[420px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
                <div className="text-center">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-red-500" />
                  <p className="mt-3 text-sm text-slate-600">Chargement de l’aperçu PDF...</p>
                </div>
              </div>
            ) : previewError ? (
              <div
                data-testid="label-preview-error"
                className="flex min-h-[420px] items-center justify-center rounded-xl border border-red-200 bg-red-50 p-6 text-center"
              >
                <div className="max-w-md">
                  <p className="text-lg font-semibold text-red-700">Aperçu indisponible</p>
                  <p className="mt-2 text-sm text-red-600">{previewError}</p>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <iframe
                  title="Aperçu de l'étiquette"
                  data-testid="label-preview-frame"
                  src={previewUrl || undefined}
                  className="h-[65vh] w-full bg-white"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeliveryLabelDialog;
